import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';

import { get, getHgproBaseurl } from '../../utils/service';
import { AUTH_STATUS, useAuth } from '../Auth';

const baseurl = getHgproBaseurl(window.location.hostname);
const MENU_ITEMS = [
  {
    href: 'https://helpcenter.healthgrades.com/?utm_source=hgmd&utm_medium=footer&utm_campaign=help-center&redirected=true',
    icons: {
      default: 'HelpCenter.svg',
      hover: 'HelpCenterHover.svg',
      active: 'HelpCenterActive.svg',
    },
    label: 'Help Center',
  },
  {
    href: `${baseurl}/account/settings`,
    icons: {
      default: 'AccountSetting.svg',
      hover: 'AccountSettingHover.svg',
      active: 'AccountSettingActive.svg',
    },
    label: 'Account Settings',
  },
  {
    href: `${baseurl}/account/ChangePassword`,
    icons: {
      default: 'ChangePassword.svg',
      hover: 'ChangePasswordHover.svg',
      active: 'ChangePasswordActive.svg',
    },
    label: 'Change Password',
  },
];

function iconPath(name) {
  return `/public/images/hamburger-menu/${name}`;
}

const MenuItem = ({ as = 'button', className, children, ...props }) => {
  return (
    <Menu.Item
      as={as}
      {...props}
      className={({ active }) =>
        [
          'flex items-center py-3 px-4 gap-2.5 select-none w-full',
          'justify-start text-left',
          'font-thin cursor-pointer',
          'hover:bg-soft-blue hover:text-blue-30',
          active ? 'bg-soft-blue text-blue-30' : 'text-gray-900',
          'group',
          className,
        ].join(' ')
      }
    >
      {children}
    </Menu.Item>
  );
};

const MenuLink = ({ href, icons, label }) => (
  <MenuItem as="a" href={href}>
    {({ active }) => (
      <>
        {/* Default state */}
        <img
          src={iconPath(icons.default)}
          alt={label}
          className={`w-[30px] h-[30px] ${active ? 'hidden' : 'block group-hover:hidden group-active:hidden'}`}
        />
        {/* Hovered state */}
        <img
          src={iconPath(icons.hover)}
          alt={`${label} Hover`}
          className={`w-[30px] h-[30px] ${active ? 'block group-active:hidden' : 'hidden group-hover:block group-active:hidden'}`}
        />
        {/* Active state */}
        <img
          src={iconPath(icons.active)}
          alt={`${label} Active`}
          className="w-[30px] h-[30px] hidden group-active:block"
        />
        <span>{label}</span>
      </>
    )}
  </MenuItem>
);

const SignOutMenuItem = ({ onClick }) => (
  <MenuItem as="button" type="button" onClick={onClick}>
    <span className="w-full">Sign Out</span>
  </MenuItem>
);

const Logo = ({ className, variant }) => {
  const src =
    variant === 'mobile'
      ? '/public/images/healthgrades-logo-trademark_WithProfessional_mobile.svg'
      : '/public/images/healthgrades-logo-trademark_WithProfessional.svg';
  return <img src={src} alt="Healthgrades" className={className} />;
};

const MenuToggle = ({ open }) => {
  const icon = open
    ? '/public/images/hamburger-menu/x-mark.svg'
    : '/public/images/hamburger-menu/hamburger-menu.png';
  const alt = open ? 'Close menu' : 'Open menu';
  return <img src={icon} alt={alt} className="cursor-pointer h-6 w-6" />;
};

const Header = () => {
  const { status } = useAuth();

  const redirectToSignUp = () => {
    window.location.href = `${baseurl}/account/register?fromSource=self-serve&redirect_uri=${encodeURIComponent(window.location.origin)}&pCID=self-serve`;
  };

  //Redirects user after successful sign-in to the HGPro page
  const redirectToLogIn = () => {
    window.location.href = `${baseurl}`;
  };

  const signOut = () => {
    get(`${baseurl}/api/account/self-serve-sign-out`)
      .then((_response) => {
        window.location.href = '/';
      })
      .catch((error) => {
        console.error('Sign out failed:', error);
      });
  };

  return (
    <div className="relative flex h-15 px-5 py-3 w-full md:h-16 md:px-8 justify-between items-center bg-white text-gray-90 font-hg-sans">
      <Logo className="h-auto hidden md:block" variant="desktop" />
      <Logo className="h-auto block md:hidden" variant="mobile" />

      <div className="flex items-center gap-4">
        {status === AUTH_STATUS.UNAUTHENTICATED ? (
          <>
            <button
              type="button"
              className="flex px-6 justify-center items-center text-deep-blue-30 text-xs font-hg-sans md:text-sm font-bold hover:text-deep-blue-50 transition-colors duration-200"
              onClick={redirectToLogIn}
            >
              Log in
            </button>
            <button
              type="button"
              className="flex px-6 py-2 md:px-8 md:py-3 justify-center items-center rounded-full border border-deep-blue-30 text-deep-blue-30 bg-white hover:bg-deep-blue-30 hover:text-white transition-colors duration-200 text-xs font-hg-sans md:text-sm font-bold"
              onClick={redirectToSignUp}
            >
              Sign Up
            </button>
          </>
        ) : (
          <Menu as="div" className="relative inline-block text-left">
            <Menu.Button className="flex items-center justify-center">
              {({ open }) => <MenuToggle open={open} />}
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute top-[42px] mt-0 right-[-1.25rem] w-[calc(100vw-15px)] bg-white p-[10px] shadow-lg border-t border-gray-200 z-99 focus:outline-none md:top-[44px] md:right-[-2rem] md:w-auto md:min-w-[375px]">
                <div className="flex flex-col gap-5 py-2.5 border-b mb-2.5 border-gray-200">
                  {MENU_ITEMS.map((item) => (
                    <MenuLink key={item.label} {...item} />
                  ))}
                </div>
                <SignOutMenuItem onClick={signOut} />
              </Menu.Items>
            </Transition>
          </Menu>
        )}
      </div>
    </div>
  );
};

export default Header;
